import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Page from "../../common/layout/Page";
import Control from "../../common/Control";
import Loader from "../../common/Loader";
import Pagination from "../../common/Pagination";
import SaveBtn from "../../common/SaveBtn";
import Table from "../../common/Table";
import tools from "../../../helpers/tools";
import dataService from "../../../helpers/dataService";
import StatutExportBadge from "./components/StatutExportBadge";
import DemanderExportForm from "./components/DemanderExportForm";
import { STATUTS_EXPORT, TYPES_EXPORT } from "./components/exportConstants";

const FILTRES_VIDES = {
  type: "",
  statut: "",
};

const PAGINATION_INITIALE = { page: 1, nbItemsByPage: 25 };

/**
 * Page de supervision : TOUS les exports demandés, avec leur demandeur. Le
 * téléchargement, lui, reste strictement réservé au demandeur de chaque
 * export (page /exports/:id, contrôle fait par l'API).
 */
const ExportList = () => {
  // `filtres` est la saisie en cours, `filtresAppliques` ce qui a réellement
  // été envoyé à l'API — seul le second déclenche une requête.
  const [filtres, setFiltres] = useState(FILTRES_VIDES);
  const [filtresAppliques, setFiltresAppliques] = useState(FILTRES_VIDES);
  const [pagination, setPagination] = useState(PAGINATION_INITIALE);

  const [exports, setExports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState(false);
  const [nbPages, setNbPages] = useState(1);
  const [nbResults, setNbResults] = useState(0);

  useEffect(() => {
    searchExports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtresAppliques, pagination]);

  function searchExports() {
    setIsLoading(true);
    setErrors(false);
    const parPage = parseInt(pagination.nbItemsByPage);
    dataService.post(
      "exports/search",
      {
        type: filtresAppliques.type || null,
        statut: filtresAppliques.statut || null,
        page: pagination.page,
        parPage,
      },
      (data) => {
        setExports(data.exports);
        setNbResults(data.total);
        setNbPages(Math.max(1, Math.ceil(data.total / parPage)));
      },
      setErrors,
      () => setIsLoading(false)
    );
  }

  // Suppression manuelle (SUPER_ADMIN uniquement, CE-1289) : le fichier est
  // détruit puis l'export disparaît de la liste. L'API refuse un export en
  // cours de préparation (409) — le message d'erreur est affiché tel quel.
  function supprimerExport(id) {
    dataService.remove(`exports/${id}`, {}, () => {
      searchExports();
    });
  }

  function updateFilter(e) {
    const { name, value } = e.target;
    setFiltres((f) => ({ ...f, [name]: value }));
  }

  function appliquerFiltres(e) {
    if (e) e.preventDefault();
    setFiltresAppliques(filtres);
    setPagination((p) => ({ ...p, page: 1 }));
  }

  function viderFiltres() {
    setFiltres(FILTRES_VIDES);
    setFiltresAppliques(FILTRES_VIDES);
    setPagination((p) => ({ ...p, page: 1 }));
  }

  function changePage(i) {
    setPagination((p) => ({ ...p, page: i }));
  }

  function changeNbItemsByPage(i) {
    setPagination({ page: 1, nbItemsByPage: i });
  }

  return (
    <>
      <Page container="container-fluid px-5" title="Suivi des exports">
        <DemanderExportForm onExportDemande={searchExports} />

        <form className="row align-items-end mb-2" onSubmit={appliquerFiltres}>
          <div className="col-12 col-md-3">
            <Control
              type="select"
              label="Type"
              name="type"
              value={filtres.type}
              change={updateFilter}
              datas={TYPES_EXPORT}
              selectFirstLabel="Tous"
            />
          </div>
          <div className="col-12 col-md-3">
            <Control
              type="select"
              label="Statut"
              name="statut"
              value={filtres.statut}
              change={updateFilter}
              datas={STATUTS_EXPORT}
              selectFirstLabel="Tous"
            />
          </div>
          <div className="col-12 col-md-6 d-flex align-items-end justify-content-end mt-2 mt-md-0 align-with-control-input">
            <span className="text-muted mr-3 align-self-center">
              {nbResults} export{nbResults > 1 ? "s" : ""} trouvé
              {nbResults > 1 ? "s" : ""}
            </span>
            <button
              type="button"
              className="btn btn-default mr-2"
              onClick={viderFiltres}
            >
              Vider les filtres
            </button>
            <SaveBtn
              save={appliquerFiltres}
              text="Rechercher"
              type="btn-primary"
              typeBtn="submit"
              margin=""
              isSaving={isLoading}
            />
          </div>
        </form>
      </Page>
      <div className="col-12 p-4">
        {errors && errors.other ? (
          <div className="text-danger m-auto text-center">{errors.other}</div>
        ) : isLoading ? (
          <Loader />
        ) : (
          <Table
            bgTransparent
            datas={exports}
            fields={[
              {
                name: "Demandé le",
                path: "demandeLe",
                render: (el) =>
                  el ? tools.formatDate(el, "dd/MM/yyyy HH:mm") : "-",
              },
              { name: "Type", path: "typeLabel" },
              {
                name: "Demandeur",
                path: "demandeurNom",
                render: (el) => el || "-",
              },
              {
                name: "Statut",
                path: "*",
                render: (el) => (
                  <>
                    <StatutExportBadge
                      statut={el.statut}
                      statutLabel={el.statutLabel}
                    />
                    {el.statut === "ECHEC" && el.motifEchec && (
                      <div className="text-danger small mt-1">
                        {el.motifEchec}
                      </div>
                    )}
                  </>
                ),
              },
              {
                name: "Disponible jusqu'au",
                path: "expireLe",
                render: (el) =>
                  el ? tools.formatDate(el, "dd/MM/yyyy HH:mm") : "-",
              },
              {
                name: "Fichier",
                path: "nomFichier",
                render: (el) => el || "-",
              },
              {
                name: "Détail",
                path: "id",
                render: (elem) => (
                  <Link
                    className="btn btn-primary btn-sm align-items-center justify-content-center d-flex"
                    to={"/exports/" + elem}
                  >
                    <i className="fa fa-search"></i>
                  </Link>
                ),
              },
              {
                name: "Supprimer",
                path: "id",
                render: (elem) => (
                  <div data-priv="delete_exports">
                    <button
                      className="btn btn-danger btn-sm"
                      title="Supprimer définitivement cet export et son fichier"
                      onClick={() => supprimerExport(elem)}
                    >
                      <i className="fa fa-times" />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
        <Pagination
          nbPages={nbPages}
          page={pagination.page}
          changePage={changePage}
          nbItemsByPage={pagination.nbItemsByPage}
          changeNbItemsByPage={changeNbItemsByPage}
        />
      </div>
    </>
  );
};

export default ExportList;
